import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { UploadFileProps } from '@wellesley/ui'
import { UploadItem } from '@wellesley/uploads'
import { SelectionView } from './SelectionView'
import { UploadingView } from './UploadingView'
import { useDialogPosition } from './useDialogPosition'
import { useDialogDismiss } from './useDialogDismiss'
import { useUploadBatch } from './useUploadBatch'

type InsertMediaDialogProps = {
  onAddMedia?: (files: UploadFileProps[]) => void
  attachments?: UploadItem[]
  anchorRect?: DOMRect
  onClose: () => void
  onInsertUploads: (items: UploadItem[]) => void
  onInsertExternalUrl: (url: string) => void
  onInsertAttachments: (items: UploadItem[]) => void
  onAllCanceled: () => void
}

export const InsertMediaDialog = ({
  onAddMedia,
  attachments = [],
  anchorRect,
  onClose,
  onInsertUploads,
  onInsertExternalUrl,
  onInsertAttachments,
  onAllCanceled,
}: InsertMediaDialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null)

  const position = useDialogPosition({ dialogRef, anchorRect })
  const { uploadingItems, isUploading, startUploadBatch } =
    useUploadBatch(attachments)
  useDialogDismiss({ dialogRef, onClose, disabled: isUploading })

  const handleLocalFileUpload = (files: UploadFileProps[]) => {
    startUploadBatch()
    onAddMedia?.(files)
  }

  const handleUploadComplete = (completedItems: UploadItem[]) => {
    onInsertUploads(completedItems)
    onClose()
  }

  const handleAllUploadsCanceled = () => {
    onAllCanceled()
    onClose()
  }

  const handleExternalUrlInsert = (url: string) => {
    onInsertExternalUrl(url)
    onClose()
  }

  const handleAttachmentsInsert = (
    selectedIds: string[],
    allAttachments: UploadItem[],
  ) => {
    const selectedItems = allAttachments.filter((att) =>
      selectedIds.includes(att.uploadId || att.id),
    )
    onInsertAttachments(selectedItems)
    onClose()
  }

  const dialog = (
    <div
      ref={dialogRef}
      className="fixed z-[200] w-[400px] rounded-lg border bg-white shadow-lg"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      {isUploading ? (
        <UploadingView
          uploadingItems={uploadingItems}
          onComplete={handleUploadComplete}
          onAllCanceled={handleAllUploadsCanceled}
        />
      ) : (
        <SelectionView
          attachments={attachments}
          onLocalFileUpload={handleLocalFileUpload}
          onExternalUrlInsert={handleExternalUrlInsert}
          onAttachmentsInsert={handleAttachmentsInsert}
        />
      )}
    </div>
  )

  return createPortal(dialog, document.body)
}
